import React, { ChangeEvent, useEffect, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import type { NextPage } from 'next';

import { TextareaAutosize } from '@mui/material';
import { FormControl, RadioGroup, FormControlLabel, Radio, Typography } from '@mui/material';

import { useRecoilValue } from 'recoil';

import { useAgreementReports } from '@hooks_iqfu/dashboard/useAgreementReports';

import { outputAlertMessage } from '@utils_iqfu/outputAlertMessage';

import ConsoleChatPageContainer from '@components_iqfu/chat/common/chat_page_container/ConsoleChatPageContainer';
import ConsoleChatMainContainer from '@components_iqfu/chat/common/main_container/ConsoleChatMainContainer';
import CheckBox from '@components_iqfu/dashboard/check_box/CheckBox';
import DatePickerWrapper from '@components_iqfu/dashboard/date_picker_wrapper/DatePickerWrapper';
import RequiredLabel from '@components_iqfu/dashboard/required_label/RequiredLabel';
import SelectBoxWrapper from '@components_iqfu/dashboard/selectBox/SelectBoxWrapper';
import UploadPdfArea from '@components_iqfu/dashboard/upload_files/UploadPdfArea';

import { companyLoginUserDataSelector } from '@recoil_iqfu/atoms/auth/companyLoginUserData';

import { AgreementReportFileType, CompanyType, DealType, NotificationText } from '@constants_iqfu';
import { useNotificationContext } from '@contexts_common/notificationContext';
import styles from '@styles_iqfu/chat/agreement_reports.module.scss';
import { handleInputText } from '@utils_common/handleInputText';

const AgreementReports: NextPage = () => {
  const { createAgreementReport } = useAgreementReports();
  const { setNotificationDisplay, setNotificationMessage, setNotificationStatus } = useNotificationContext();

  const { companyId, isMultipleCompanyUser, multipleCompaniesOptions, companyType, commissionRateTypeKey } =
    useRecoilValue(companyLoginUserDataSelector);

  const [selectedCompany, setSelectedCompany] = useState(null);

  const router = useRouter();

  // クエリパラメータからchat_room_id / offline_seller_lead_id / seller_nameを取得
  const { chat_room_id, offline_seller_lead_id, seller_name: querySellerName } = router.query;
  const chatRoomId = chat_room_id ? Number(chat_room_id) : null;
  const offlineSellerLeadId = offline_seller_lead_id ? Number(offline_seller_lead_id) : null;

  const [disabled, setDisabled] = useState(true);
  const [sellerName, setSellerName] = useState('');
  const [ownerName, setOwnerName] = useState('');

  // クエリパラメータから売主名・名義人名を取得して初期値として設定
  useEffect(() => {
    if (router.isReady) {
      const { seller_name, owner_name } = router.query;
      if (typeof seller_name === 'string' && seller_name) {
        setSellerName(seller_name);
      }
      if (typeof owner_name === 'string' && owner_name) {
        setOwnerName(owner_name);
      }
    }
  }, [router.isReady, router.query]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [settlementDate, setSettlementDate] = useState<any>(null);
  const [settlementDateUnconfirmed, setSettlementDateUnconfirmed] = useState(false);
  const [dealType, setDealType] = useState<string>('mediation');
  const [note, setNote] = useState('');
  const [salesAgreements, setSalesAgreements] = useState<Blob[]>([]);
  const [commissionReceipt, setCommissionReceipt] = useState<Blob[]>([]);
  const [mediationAgreement, setMediationAgreement] = useState<Blob[]>([]);

  const submitUser = async () => {
    setDisabled(true);
    const formData = new FormData();
    if (isMultipleCompanyUser) {
      selectedCompany && formData.append('company_id', String(selectedCompany));
    } else {
      formData.append('company_id', String(companyId));
    }

    // deal_typeとpurchase_typeの設定
    if (dealType === 'mediation') {
      formData.append('deal_type', 'mediation');
    } else {
      formData.append('deal_type', 'purchase');
      formData.append('purchase_type', dealType); // 'other_company' or 'in_house'
    }

    formData.append('seller', sellerName);
    formData.append('owner_name', ownerName);
    formData.append('settlement_date', settlementDate);
    formData.append('settlement_date_confirmed', (!settlementDateUnconfirmed).toString());

    salesAgreements.forEach((file) => {
      formData.append('files[][file]', file);
      formData.append('files[][file_type]', 'sales_agreement');
    });
    commissionReceipt.forEach((file) => {
      formData.append('files[][file]', file);
      formData.append('files[][file_type]', 'commission_receipt');
    });
    mediationAgreement.forEach((file) => {
      formData.append('files[][file]', file);
      formData.append('files[][file_type]', 'mediation_agreement');
    });
    formData.append('note', note);

    if (offlineSellerLeadId) {
      formData.append('reportable_type', 'OfflineSeller::Lead');
      formData.append('reportable_id', String(offlineSellerLeadId));
    } else if (chatRoomId) {
      formData.append('chat_room_id', String(chatRoomId));
    }

    const result = await createAgreementReport(formData);
    if (result.id) {
      setNotificationStatus('success');
      setNotificationMessage(NotificationText.successCreateAgreementReport);
      setNotificationDisplay(true);
      router.push(`/console/company/chat/agreement_reports`);
    } else {
      setNotificationStatus('alert');
      setNotificationMessage(outputAlertMessage(result));
      setNotificationDisplay(true);
    }
    setDisabled(false);
  };

  const removeFileHandler = (removalId: number, fileType: keyof typeof AgreementReportFileType) => {
    if (fileType === 'sales_agreement') {
      setSalesAgreements((files) => files.filter((_file: any, i: number) => i !== removalId));
    } else if (fileType === 'commission_receipt') {
      setCommissionReceipt((files) => files.filter((_file: any, i: number) => i !== removalId));
    } else {
      setMediationAgreement((files) => files.filter((_file: any, i: number) => i !== removalId));
    }
  };

  // クエリパラメータから売主名をプリフィル
  useEffect(() => {
    if (!isInitialized && querySellerName && typeof querySellerName === 'string') {
      setSellerName(querySellerName);
      setIsInitialized(true);
    }
  }, [querySellerName, isInitialized]);

  useEffect(() => {
    if (
      sellerName === '' ||
      settlementDate === null ||
      salesAgreements.length === 0 ||
      (isMultipleCompanyUser && selectedCompany === null)
    ) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [sellerName, ownerName, settlementDate, salesAgreements, commissionReceipt, mediationAgreement, selectedCompany]);

  const imageUrls: Record<keyof typeof CompanyType, Record<string, string>> = {
    general_agency: {
      standard: '/images/iqfu/dashboard/agreement_reports/general_agency_standard.png',
      c21: '/images/iqfu/dashboard/agreement_reports/general_agency_c21.png',
    },
    purchase_agency: {
      standard: '/images/iqfu/dashboard/agreement_reports/purchase_agency_standard.png',
      asolie: '/images/iqfu/dashboard/agreement_reports/purchase_agency_asolie.png',
    },
  };

  const imageUrl = imageUrls[companyType]?.[commissionRateTypeKey as keyof typeof imageUrls[typeof companyType]] || '';

  return (
    <>
      <ConsoleChatPageContainer userType="company" limitHeightToWindowHeight={false}>
        <ConsoleChatMainContainer>
          <div className={styles.page_container}>
            <div className={styles.title_wrap}>
              <h3 className={styles.section_title}>売買契約報告</h3>
            </div>

            <div className={styles.report_lead}>
              <span className={styles.text_red}>イクラ不動産よりご紹介した顧客様</span>
              と売買契約を締結されましたら、必ずこちらのフォームから1週間以内にご報告が必要です。　
              <br />
              その他の売却事例の登録は、
              <Link href={'/console/company/sale_cases/create'}>
                <a className={styles.link_text}>売却実績登録</a>
              </Link>
              からお願いいたします。
            </div>

            <img src={imageUrl} alt="紹介手数料算出方法" className={styles.commission_calculation} />

            <div className={styles.input_item}>
              <h2 className={styles.input_item_label}>取引タイプ</h2>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  name="row-radio-buttons-group"
                  className={`${styles.radio} ${styles.radio_group_width1}`}
                  value={dealType}
                  onChange={(event) => {
                    setDealType(event.target.value as keyof typeof DealType);
                  }}
                >
                  <FormControlLabel value="mediation" control={<Radio />} label="売り仲介" />
                  <FormControlLabel value="other_company" control={<Radio />} label="買取会社への仲介" />
                  <FormControlLabel value="in_house" control={<Radio />} label="自社買取" />
                </RadioGroup>
              </FormControl>
            </div>

            {isMultipleCompanyUser && (
              <div className={styles.input_item}>
                <label className={styles.input_item_label}>
                  会社名
                  <RequiredLabel />
                </label>
                <SelectBoxWrapper
                  selectList={multipleCompaniesOptions}
                  state={selectedCompany}
                  setState={setSelectedCompany}
                  placeholder="会社選択"
                  size="width1"
                />
              </div>
            )}

            <div className={styles.input_item}>
              <label className={styles.input_item_label}>
                売主名
                <RequiredLabel />
              </label>
              <input
                className={styles.input_text_width1}
                type="text"
                maxLength={30}
                placeholder="売主名"
                value={sellerName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleInputText(event, setSellerName);
                }}
              />
            </div>

            <div className={styles.input_item}>
              <label className={styles.input_item_label}>名義人名</label>
              <input
                className={styles.input_text_width1}
                type="text"
                maxLength={30}
                placeholder="名義人名"
                value={ownerName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleInputText(event, setOwnerName);
                }}
              />
            </div>

            <div className={styles.input_item}>
              <label className={`${styles.input_item_label} ${styles.with_input_annotation}`}>
                残代金決済日
                <RequiredLabel />
                <br />
                <span className={`${styles.input_item_label} ${styles.input_item_label_note}`}>
                  ※決済日が確定していない場合、契約書の残代金の日付を選択のうえ「確定の決済日ではありません」にチェックを入れてください
                </span>
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <DatePickerWrapper
                  state={settlementDate}
                  setState={setSettlementDate}
                  inputType="day"
                  outputType="day"
                  size="width1"
                />
                <CheckBox
                  checked={settlementDateUnconfirmed}
                  onClick={() => {
                    setSettlementDateUnconfirmed(!settlementDateUnconfirmed);
                  }}
                  label="確定の決済日ではありません"
                />
              </div>
            </div>

            <div className={styles.input_item}>
              <label className={styles.input_item_label}>
                売買契約書
                <RequiredLabel />
              </label>
              <div className={styles.upload_area}>
                <UploadPdfArea files={salesAgreements} setFiles={setSalesAgreements} fileCategory={'売買契約書'} />
                {salesAgreements.map((file: any, i) => {
                  return (
                    <div key={file.name + i} className={styles.attached_file_wrap}>
                      {file.name}
                      <button className={styles.delete_button} onClick={() => removeFileHandler(i, 'sales_agreement')}>
                        削除
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {(dealType === 'mediation' || dealType === 'other_company') && (
              <div className={styles.input_item}>
                <label className={styles.input_item_label}>
                  仲介手数料領収書
                  <br />
                  または
                  <br />
                  仲介手数料の支払約定書
                  <br />
                  <span className={`${styles.input_item_label} ${styles.input_item_label_note}`}>
                    ※取得済みでしたら添付お願いします
                  </span>
                </label>
                <div className={styles.upload_area}>
                  <UploadPdfArea
                    files={commissionReceipt}
                    setFiles={setCommissionReceipt}
                    fileCategory={'仲介手数料領収書\n仲介手数料の支払約定書'}
                  />
                  {commissionReceipt.map((file: any, i) => {
                    return (
                      <div key={file.name + i} className={styles.attached_file_wrap}>
                        {file.name}
                        <button
                          className={styles.delete_button}
                          onClick={() => removeFileHandler(i, 'commission_receipt')}
                        >
                          削除
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(dealType === 'mediation' || dealType === 'other_company') && (
              <div className={styles.input_item}>
                <label className={styles.input_item_label}>
                  媒介契約書
                  <br />
                  <span className={`${styles.input_item_label} ${styles.input_item_label_note}`}>
                    ※媒介契約報告済みの場合は不要です
                  </span>
                </label>
                <div className={styles.upload_area}>
                  <UploadPdfArea
                    files={mediationAgreement}
                    setFiles={setMediationAgreement}
                    fileCategory={'媒介契約書'}
                  >
                    <Typography sx={{ fontSize: '1.6rem', mt: 1 }}>全てのページをアップロードしてください</Typography>
                  </UploadPdfArea>
                  {mediationAgreement.map((file: any, i) => {
                    return (
                      <div key={file.name + i} className={styles.attached_file_wrap}>
                        {file.name}
                        <button
                          className={styles.delete_button}
                          onClick={() => removeFileHandler(i, 'mediation_agreement')}
                        >
                          削除
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className={styles.input_item}>
              <label className={styles.input_item_label}>備考</label>
              <TextareaAutosize
                value={note}
                minRows={3}
                placeholder="特記事項などございましたら、こちらに入力してください。"
                maxLength={200}
                className={styles.input_text_area}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  setNote(event.target.value);
                }}
              />
            </div>

            <button
              className={`${styles.action_button} ${disabled && styles.inactive}`}
              type="submit"
              disabled={disabled}
              onClick={submitUser}
            >
              保存
            </button>
          </div>
        </ConsoleChatMainContainer>
      </ConsoleChatPageContainer>
    </>
  );
};

export default AgreementReports;
